// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 아주 작은 라우터
//
//  ★ react-router 를 안 썼다. 이유는 셋이다.
//    ① 지금 화면이 셋(로그인 · 목록 · 404)이고 나머지는 「준비 중」 자리표시자다.
//       경로표 하나면 끝난다 — `apps/api/src/index.ts` 가 hono 를 안 쓴 것과 같은 판단이다.
//    ② react-router v7 은 지금(2026-08) `npm audit` 에 **high 경고가 남는다.**
//       최신판(7.18)은 RSC 모드 CSRF, 그 아래는 XSS·오픈리다이렉트 무더기다.
//       우리는 정적 SPA 라 실제로 안 맞지만, 보안을 깐깐히 보는 저장소에서
//       "audit 에 빨간 줄이 늘 떠 있는" 상태는 **진짜 경고를 못 보게 만든다.**
//    ③ 화면이 열 개가 넘고 중첩 라우트·데이터 로더가 필요해지면 그때 넣으면 된다.
//       이 파일이 40줄이라 그때 버리는 비용이 거의 0이다.
//
//  ⚠️ **history API 를 쓴다 = 딥링크를 서버가 index.html 로 받아 줘야 한다.**
//     `/dangjin` 을 새 탭에서 바로 열었을 때 서버가 index.html 을 안 주면
//     **모든 딥링크가 서버 404** 다.
//
//  ★★ [고침 2026-08-12] 여기 원래 「Cloudflare Pages 라면 `_redirects` 한 줄이다」라고
//     적혀 있었다. **틀린 말이고, 그대로 따라 하면 화면이 하얗게 뜬다.**
//
//     ① 우리는 **이미 된다.** Cloudflare 공식 문서:
//        *"If a project lacks a top-level `404.html` file, Pages assumes it's a
//        single-page application (SPA) … all incoming paths are matched to the root (`/`)"*
//        `apps/web/public/` 에 `404.html` 이 없으므로 `/dangjin`·`/s/{열쇠}`·`/join` 은
//        **파일 한 줄 없이 이미** index.html 을 받는다.
//     ② 오히려 **적으면 위험하다.** `_redirects` 규칙은 공식 문서상
//        *"always followed regardless of whether an asset matches"* 라,
//        `/*  /index.html  200` 은 해시 붙은 `/assets/*.js`·`.css` 요청까지 index.html 로
//        덮는다. 증상은 **빈 흰 화면**인데 서버 로그에는 200 만 남아 원인이 안 보인다.
//
//     왜 예전엔 그렇게 믿었나 — Netlify·Vercel 은 SPA 폴백을 rewrite **한 줄로 적는 것**이
//     정석이라 그 습관이 그대로 옮겨 왔다. Pages 는 그 한 줄이 **기본 동작**이라 다르다.
//
//     → 그래서 할 일은 파일을 **만드는** 게 아니라 `404.html` 이 **안 생기게** 지키는 것이다.
//       (생기는 순간 Pages 가 SPA 취급을 그만둬 공유·초대 링크가 통째로 404 다.)
//       둘 다 `apps/web/src/routeContract.spec.ts` 가 시험으로 붙든다. README §7 참고.
//
//     ⚠️ `/api/*`·`/asset/*`·`/invite/*` 를 Worker 가 받게 하는 건 **완전히 별개 문제**이고,
//        `_redirects` 가 아니라 `apps/api/wrangler.toml` 의 `[[env.production.routes]]` 가 정한다.
// ─────────────────────────────────────────────────────────────────────────

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';

/** navigate() 가 스스로 알리는 사설 이벤트. popstate 는 pushState 때 안 뜬다. */
const NAV_EVENT = 'cv:navigate';

const PathContext = createContext<string>('/');

/** 지금 경로. 항상 `/` 로 시작하고 쿼리·해시는 들어 있지 않다. */
export function usePath(): string {
  return useContext(PathContext);
}

/**
 * 주소를 바꾼다.
 *
 * ⚠️ React 컴포넌트 **밖에서도** 부를 수 있게 훅이 아니라 함수로 뒀다.
 *    (로그인 성공 직후처럼 렌더 바깥에서 부르는 자리가 실제로 있다.)
 */
export function navigate(to: string, opts?: { replace?: boolean }): void {
  if (to === window.location.pathname) return;
  if (opts?.replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  window.dispatchEvent(new Event(NAV_EVENT));
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState<string>(() => window.location.pathname);

  useEffect(() => {
    const sync = () => setPath(window.location.pathname);
    // popstate : 뒤로/앞으로 버튼
    // NAV_EVENT: 우리 navigate()
    window.addEventListener('popstate', sync);
    window.addEventListener(NAV_EVENT, sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener(NAV_EVENT, sync);
    };
  }, []);

  useEffect(() => {
    // 화면을 갈아 끼웠는데 스크롤이 중간에 남아 있으면 "아무 일도 안 일어난 것" 처럼 보인다.
    window.scrollTo(0, 0);
  }, [path]);

  return <PathContext.Provider value={path}>{children}</PathContext.Provider>;
}

/**
 * 내부 링크.
 *
 * ★ **진짜 `<a href>` 다.** 가운데 클릭·Ctrl+클릭으로 새 탭에 열리고, 마우스를 올리면
 *   상태표시줄에 주소가 뜨고, 스크린리더가 링크로 읽는다. `<div onClick>` 으로 만들면
 *   이 셋이 전부 사라지는데, 없어졌다는 걸 아무도 신고하지 않는다.
 */
export function A({
  href,
  children,
  ...rest
}: { href: string; children: ReactNode } & Omit<
  React.ComponentPropsWithoutRef<'a'>,
  'href' | 'children'
>) {
  function onClick(e: MouseEvent<HTMLAnchorElement>) {
    // 새 탭·새 창으로 여는 조작은 브라우저에 그대로 넘긴다.
    if (e.defaultPrevented) return;
    if (e.button !== 0) return; // 가운데·오른쪽 버튼
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(href);
  }
  return (
    <a href={href} onClick={onClick} {...rest}>
      {children}
    </a>
  );
}
